/**
 * KvkkConsentGate — Full-screen blocking modal shown to doctors who haven't
 * accepted the in-app KVKK form yet. Cannot be dismissed without acceptance.
 *
 * Mounts inside DashboardLayout so it appears on top of any doctor page.
 * Calls POST /api/auth/kvkk-consent on submit and updates auth context.
 */
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchWithAuth } from '../lib/api';
import { KVKK_TEXT_DOCTOR } from '../lib/kvkk-text';
import { Button } from './ui/button';
import { Loader2, ShieldCheck, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function KvkkConsentGate() {
  const { user, updateUser, logout } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Gate only applies to doctor + clinic_admin roles who have not consented yet.
  // Super admin, credit admin, and employees skip (internal staff).
  const needsConsent =
    user &&
    ['doctor', 'clinic_admin'].includes(user.role) &&
    user.kvkk_in_app_consent !== true;

  if (!needsConsent) return null;

  const onAccept = async () => {
    if (!accepted) {
      toast.error('Lütfen önce metni okuyup kutucuğu işaretleyin.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetchWithAuth('/auth/kvkk-consent', { method: 'POST' });
      updateUser({
        kvkk_in_app_consent: true,
        kvkk_in_app_consent_date: res?.kvkk_in_app_consent_date,
      });
      toast.success('Onayınız kaydedildi. Hoş geldiniz!');
    } catch (err) {
      toast.error(err?.message || 'Kaydedilemedi, lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
      data-testid="kvkk-consent-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kvkk-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-start gap-3 flex-shrink-0">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2
              id="kvkk-title"
              className="text-lg font-bold text-slate-800"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              KVKK Aydınlatma & Onay
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Devam edebilmek için kişisel verilerinizin işlenmesine ilişkin metni
              onaylamanız gerekmektedir.
            </p>
          </div>
        </div>

        {/* Scrollable text body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 bg-slate-50/50">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
              <FileText className="w-3.5 h-3.5" />
              Aydınlatma Metni
            </div>
            <pre
              className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap font-sans"
              data-testid="kvkk-consent-text"
            >
              {KVKK_TEXT_DOCTOR}
            </pre>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex-shrink-0 bg-white space-y-3">
          <label className="flex items-start gap-2.5 cursor-pointer select-none p-3 rounded-lg bg-amber-50 border border-amber-200 hover:bg-amber-100/70 transition-colors">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4"
              data-testid="kvkk-consent-checkbox"
              disabled={submitting}
            />
            <span className="text-sm text-amber-900 font-medium leading-snug">
              Aydınlatma metnini okudum, anladım ve kişisel verilerimin belirtilen
              şekilde işlenmesini onaylıyorum.
            </span>
          </label>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={logout}
              disabled={submitting}
              className="flex-1"
              data-testid="kvkk-consent-reject-btn"
            >
              Onaylamıyorum, çıkış
            </Button>
            <Button
              type="button"
              onClick={onAccept}
              disabled={submitting || !accepted}
              className="flex-1 bg-[#5A67D8] hover:bg-[#4338CA] text-white"
              data-testid="kvkk-consent-accept-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                'Onaylıyorum, başla'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
